import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
    User, Mail, Lock, CheckCircle, ArrowRight, ArrowLeft, 
    Baby, GraduationCap, Users, Phone, Home, BookOpen, Smile, Award, Heart, Globe, Gift,
    Zap
} from "lucide-react";

// 1. IMPORT FIREBASE SERVICES
import { auth, db } from "../firebase1"; // Adjust the path as necessary
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";

// Framer Motion Variants (Unchanged)
const formVariants = {
  enter: { opacity: 0, x: 50, scale: 0.98 },
  center: { 
    opacity: 1, 
    x: 0, 
    scale: 1,
    transition: { type: "spring", stiffness: 300, damping: 30 } 
  },
  exit: { 
    opacity: 0, 
    x: -50, 
    scale: 0.98,
    transition: { duration: 0.2 } 
  },
};

// --- Helper Components (Unchanged) ---
const Input = ({ icon: Icon, error, ...props }) => (
    <div className="relative">
        {Icon && <Icon className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${error ? 'text-red-400' : 'text-purple-400'}`} />}
        <input
            {...props}
            className={`w-full ${Icon ? 'pl-12' : 'pl-4'} pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all text-base ${error ? 'border-red-500 focus:ring-red-400' : 'border-gray-300'} ${props.readOnly ? 'bg-gray-100 text-gray-500 cursor-default' : 'bg-white'}`}
        />
    </div>
);

const Label = ({ name, icon: Icon, label, error }) => (
    <label htmlFor={name} className="block text-sm font-semibold text-gray-700 mb-2 flex justify-between">
        <span className="flex items-center gap-1.5"><Icon className="w-4 h-4 text-pink-500" /> {label}</span>
        {error && <span className="text-xs font-medium text-red-500">{error}</span>}
    </label>
);

const DetailRow = ({ label, value }) => (
    <div className="flex justify-between py-1 border-b border-gray-100 last:border-b-0">
        <span className="text-sm text-gray-500 font-medium">{label}:</span>
        <span className="text-sm font-semibold text-gray-800 text-right">{value}</span>
    </div>
);


// --- Utility Functions (Unchanged) ---

const calculateAge = (dob) => {
    if (!dob) return NaN;
    const birthday = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthday.getFullYear();
    const m = today.getMonth() - birthday.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthday.getDate())) {
        age--;
    }
    return age;
};

// --- NEW SUCCESS MESSAGE COMPONENT ---
const SuccessMessage = ({ childName, fullName }) => (
    <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", stiffness: 100 }}
        className="text-center p-8 bg-green-50 rounded-2xl border-2 border-green-300 space-y-6"
    >
        <CheckCircle className="w-16 h-16 text-green-600 mx-auto" />
        <h2 className="text-3xl font-extrabold text-gray-800">
            Registration Complete! 🎉
        </h2>
        <p className="text-lg text-gray-600">
            Welcome, <span className="font-bold text-purple-600">{fullName}</span>!
        </p>
        <p className="text-gray-700">
            The account for {childName} is all set up. You can now proceed to log in and access your new dashboard.
        </p>
        <motion.a 
            href="/login" 
            className="mt-6 inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-xl shadow-sm text-white bg-pink-600 hover:bg-pink-700 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
        >
            <Zap className="w-5 h-5 mr-2" /> Go to Login
        </motion.a>
    </motion.div>
);


// --- Main Multi-Step Registration Component ---
export default function RegisterPage() {
    const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [firebaseError, setFirebaseError] = useState('');
    // NEW STATE: To control the success view
    const [isRegistered, setIsRegistered] = useState(false); 

    const [formData, setFormData] = useState({
        // ... (formData state remains the same)
        accountType: 'parent', 
        fullName: '',
        email: '',
        password: '',
        phone: '',
        relationship: '', 
        country: '', 
        childName: '',
        childDOB: '', 
        childGender: '', 
        childGrade: '',
        learningGoal: '',
        learningStyle: '', 
    });
    
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
        setFirebaseError('');
    };

    const validateStep = () => {
        // ... (Validation logic remains the same) ...
        let newErrors = {};
        let isValid = true;
        const phoneRequired = (formData.accountType === 'parent' || formData.accountType === 'other');

        if (step === 1) {
            if (!formData.fullName) { newErrors.fullName = "Full Name is required."; isValid = false; }
            if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { newErrors.email = "Valid Email is required."; isValid = false; }
            if (formData.password.length < 8) { newErrors.password = "Password must be at least 8 characters."; isValid = false; }
            if (phoneRequired && !formData.phone) { newErrors.phone = "Phone is required."; isValid = false; }
            if (formData.accountType !== 'teacher' && !formData.relationship) { newErrors.relationship = "Relationship is required."; isValid = false; }
            if (!formData.country) { newErrors.country = "Country/Region is required."; isValid = false; } 

        } else if (step === 2) {
            const age = calculateAge(formData.childDOB); 
            
            if (!formData.childName) { newErrors.childName = "Child's Name is required."; isValid = false; }
            
            if (!formData.childDOB) { newErrors.childDOB = "Birthday is required."; isValid = false; }
            else if (isNaN(age) || age < 2 || age > 15) { 
                newErrors.childDOB = `Age must be between 2 and 15 years old. Current age: ${isNaN(age) ? 'N/A' : age}`; 
                isValid = false; 
            }
            
            if (!formData.childGrade) { newErrors.childGrade = "Grade/Level is required."; isValid = false; }
            if (!formData.learningStyle) { newErrors.learningStyle = "Learning style is required."; isValid = false; }
        }

        setErrors(newErrors);
        return isValid;
    };

    const nextStep = () => {
        if (validateStep()) {
            setStep(prev => prev + 1);
        }
    };

    const prevStep = () => {
        setStep(prev => prev - 1);
    };

    // 2. UPDATED HANDLE SUBMIT FOR FIREBASE
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (step !== 3 || !validateStep()) {
            return;
        }

        setIsLoading(true);
        setFirebaseError('');
        
        try {
            const { email, password, ...profileData } = formData;
            const calculatedAge = calculateAge(profileData.childDOB);

            // 2.1. Firebase Authentication: Create User
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // 2.2. Firestore: Store User Profile Data
            const userProfile = {
                // ... (userProfile object remains the same)
                accountType: profileData.accountType,
                fullName: profileData.fullName,
                phone: profileData.phone,
                relationship: profileData.relationship,
                country: profileData.country,
                email: email, 
                child: {
                    name: profileData.childName,
                    dob: profileData.childDOB,
                    age: calculatedAge,
                    gender: profileData.childGender,
                    grade: profileData.childGrade,
                    learningGoal: profileData.learningGoal,
                    learningStyle: profileData.learningStyle,
                },
                createdAt: new Date(),
            };
            
            await setDoc(doc(db, "users", user.uid), userProfile);

            // 2.3. Success: SET THE NEW STATE TO TRUE INSTEAD OF ALERT
            console.log("Registration Complete! User:", user.uid);
            setIsRegistered(true); 

        } catch (error) {
            console.error("Firebase Registration Error:", error);
            
            let message = "Registration failed. Please check your details.";
            if (error.code === 'auth/email-already-in-use') {
                message = "The email address is already in use by another account.";
            } else if (error.code === 'auth/invalid-email') {
                message = "The email address is not valid.";
            } else if (error.code === 'auth/weak-password') {
                message = "Password should be at least 6 characters.";
            } else if (error.code === 'auth/network-request-failed') {
                message = "Network error. Please check your internet connection and try again.";
            } else if (error.code === 'auth/operation-not-allowed') {
                message = "Email/Password sign-in is not enabled in your Firebase project settings.";
            } else if (error.code === 'auth/configuration-not-found') {
                message = "Firebase configuration error. Check your API key and project settings.";
            }
            
            setFirebaseError(message);
        } finally {
            setIsLoading(false);
        }
    };

    // Calculate age outside the component render
    const currentChildAge = formData.childDOB ? calculateAge(formData.childDOB) : '';

    // --- RENDER FUNCTIONS FOR EACH STEP (Definitions remain the same) ---
    const StepOne = ( /* ... StepOne JSX ... */
        <motion.div
            key="step1"
            variants={formVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="space-y-6"
        >
            <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 mb-4 flex items-center gap-3">
                <Users className="w-6 h-6 text-purple-600" />
                1. Your Account Details
            </h2>
            {/* Account Type Toggle (Parent/Teacher/Other) */}
            <div className="grid grid-cols-3 gap-3 mb-4" role="radiogroup">
                {['parent', 'teacher', 'other'].map((type) => (
                    <motion.button
                        key={type}
                        type="button"
                        onClick={() => handleChange({ target: { name: 'accountType', value: type } })}
                        className={`flex-1 flex items-center justify-center p-3 rounded-xl font-semibold transition-all text-xs sm:text-sm ${
                            formData.accountType === type 
                                ? 'bg-purple-600 text-white shadow-md ring-2 ring-purple-300'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        {type === 'parent' ? <Home className="w-5 h-5 mr-1" /> : type === 'teacher' ? <GraduationCap className="w-5 h-5 mr-1" /> : <Heart className="w-5 h-5 mr-1" />}
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                    </motion.button>
                ))}
            </div>
            {/* General Adult Fields */}
            <div className={errors.fullName ? 'pb-2' : ''}>
                <Label name="fullName" icon={User} label="Your Full Name" error={errors.fullName} />
                <Input type="text" name="fullName" value={formData.fullName} onChange={handleChange} placeholder="First and Last Name" icon={User} error={errors.fullName} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <div className={errors.email ? 'pb-2' : ''}>
                    <Label name="email" icon={Mail} label="Email Address" error={errors.email} />
                    <Input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="you@example.com" icon={Mail} error={errors.email} />
                </div>
                <div className={errors.password ? 'pb-2' : ''}>
                    <Label name="password" icon={Lock} label="Password (min 8 characters)" error={errors.password} />
                    <Input type="password" name="password" value={formData.password} onChange={handleChange} placeholder="••••••••" icon={Lock} error={errors.password} />
                </div>
            </div>
            {/* Country Field */}
            <div className={errors.country ? 'pb-2' : ''}>
                <Label name="country" icon={Globe} label="Country/Region" error={errors.country} />
                <Input 
                    type="text" 
                    name="country" 
                    value={formData.country} 
                    onChange={handleChange} 
                    placeholder="E.g., United States, India, Canada" 
                    icon={Globe} 
                    error={errors.country} 
                />
            </div>
            {/* Conditional Caregiver Fields (Phone and Relationship) */}
            {(formData.accountType === 'parent' || formData.accountType === 'other') && (
                <motion.div 
                    initial={{ opacity: 0, height: 0 }} 
                    animate={{ opacity: 1, height: 'auto' }}
                    className="grid sm:grid-cols-2 gap-4"
                >
                    <div className={errors.phone ? 'pb-2' : ''}>
                        <Label name="phone" icon={Phone} label="Phone Number" error={errors.phone} />
                        <Input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="(123) 456-7890" icon={Phone} error={errors.phone} />
                    </div>
                    <div className={errors.relationship ? 'pb-2' : ''}>
                        <Label name="relationship" icon={Heart} label="Relationship to Child" error={errors.relationship} />
                        <Input 
                            type="text" 
                            name="relationship" 
                            value={formData.relationship} 
                            onChange={handleChange} 
                            placeholder={formData.accountType === 'parent' ? "Mother, Father, etc." : "Guardian, Admin, etc."} 
                            icon={Heart} 
                            error={errors.relationship} 
                        />
                    </div>
                </motion.div>
            )}
        </motion.div>
    );

    const StepTwo = ( /* ... StepTwo JSX ... */
        <motion.div
            key="step2"
            variants={formVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="space-y-6"
        >
            <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 mb-4 flex items-center gap-3">
                <Baby className="w-6 h-6 text-pink-600" />
                2. Child/Student Information
            </h2>
            <div className={errors.childName ? 'pb-2' : ''}>
                <Label name="childName" icon={Smile} label="Child's Name" error={errors.childName} />
                <Input type="text" name="childName" value={formData.childName} onChange={handleChange} placeholder="First Name Only" icon={Smile} error={errors.childName} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <div className={errors.childDOB ? 'pb-2' : ''}>
                    <Label name="childDOB" icon={Gift} label="Child's Birthday" error={errors.childDOB} />
                    <Input 
                        type="date" 
                        name="childDOB" 
                        value={formData.childDOB} 
                        onChange={handleChange} 
                        icon={Gift} 
                        error={errors.childDOB} 
                        max={new Date().toISOString().split('T')[0]} 
                    />
                </div>
                <div className={errors.childDOB ? 'pb-2' : ''}>
                    <Label 
                        name="childAgeDisplay" 
                        icon={Baby} 
                        label="Child's Age" 
                        error={currentChildAge > 15 || currentChildAge < 2 ? 'Age outside range (2-15)' : null} 
                    />
                    <Input 
                        type="text" 
                        name="childAgeDisplay" 
                        value={currentChildAge === '' || isNaN(currentChildAge) ? 'Enter Birthday' : `${currentChildAge} years`}
                        readOnly
                        icon={Baby} 
                        error={currentChildAge > 15 || currentChildAge < 2} 
                    />
                </div>
            </div>
            <div className={errors.childGender ? 'pb-2' : ''}>
                <Label name="childGender" icon={Users} label="Child's Gender (Optional)" error={errors.childGender} />
                <div className="relative">
                    <select 
                        name="childGender" 
                        value={formData.childGender} 
                        onChange={handleChange} 
                        className={`w-full pl-4 pr-12 py-3 border rounded-xl focus:ring-2 focus:ring-pink-400 focus:border-pink-400 transition-all text-base appearance-none ${errors.childGender ? 'border-red-500' : 'border-gray-300'}`}
                    >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Non-binary">Non-binary</option>
                        <option value="Prefer not to say">Prefer not to say</option>
                    </select>
                    <Users className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pink-400 pointer-events-none" />
                </div>
            </div>
            <div className={errors.childGrade ? 'pb-2' : ''}>
                <Label name="childGrade" icon={BookOpen} label="Current Grade/Level" error={errors.childGrade} />
                <div className="relative">
                    <select 
                        name="childGrade" 
                        value={formData.childGrade} 
                        onChange={handleChange} 
                        className={`w-full pl-4 pr-12 py-3 border rounded-xl focus:ring-2 focus:ring-pink-400 focus:border-pink-400 transition-all text-base appearance-none ${errors.childGrade ? 'border-red-500' : 'border-gray-300'}`}
                    >
                        <option value="" disabled>Select Level</option>
                        <option value="Pre-K">Pre-K / Nursery</option>
                        <option value="K">Kindergarten</option>
                        <option value="Grade 1">Grade 1</option>
                        <option value="Grade 2">Grade 2</option>
                        <option value="Grade 3+">Grade 3 and above</option>
                    </select>
                    <BookOpen className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pink-400 pointer-events-none" />
                </div>
            </div>
            <div className={errors.learningStyle ? 'pb-2' : ''}>
                <Label name="learningStyle" icon={Award} label="Preferred Learning Style" error={errors.learningStyle} />
                <div className="relative">
                    <select 
                        name="learningStyle" 
                        value={formData.learningStyle} 
                        onChange={handleChange} 
                        className={`w-full pl-4 pr-12 py-3 border rounded-xl focus:ring-2 focus:ring-pink-400 focus:border-pink-400 transition-all text-base appearance-none ${errors.learningStyle ? 'border-red-500' : 'border-gray-300'}`}
                    >
                        <option value="" disabled>Select Primary Style</option>
                        <option value="Visual">Visual (Seeing/Reading)</option>
                        <option value="Auditory">Auditory (Hearing/Speaking)</option>
                        <option value="Kinesthetic">Kinesthetic (Doing/Touching)</option>
                        <option value="Mixed">Mixed/Unsure</option>
                    </select>
                    <Award className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pink-400 pointer-events-none" />
                </div>
            </div>
            <div>
                <Label name="learningGoal" icon={BookOpen} label="Primary Learning Goal (Optional)" />
                <textarea 
                    name="learningGoal" 
                    value={formData.learningGoal} 
                    onChange={handleChange} 
                    placeholder="E.g., Improve pronunciation, develop musical timing, gain confidence."
                    rows="3"
                    className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400 focus:border-pink-400 transition-all text-base resize-none"
                />
            </div>
        </motion.div>
    );

    const StepThree = ( /* ... StepThree JSX ... */
        <motion.div
            key="step3"
            variants={formVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="space-y-6"
        >
            <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 mb-4 flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
                3. Review & Complete
            </h2>
            
            {/* Display Firebase Error */}
            {firebaseError && (
                <div className="p-3 bg-red-100 border border-red-300 rounded-xl text-sm text-red-700 font-medium">
                    {firebaseError}
                </div>
            )}
            
            <div className="bg-purple-50 p-5 rounded-xl border border-purple-200">
                <h3 className="text-lg font-bold text-purple-600 mb-3 flex items-center gap-2"><Users className="w-5 h-5" /> Adult Profile</h3>
                <DetailRow label="Account Type" value={formData.accountType.charAt(0).toUpperCase() + formData.accountType.slice(1)} />
                <DetailRow label="Name" value={formData.fullName} />
                <DetailRow label="Email" value={formData.email} />
                <DetailRow label="Country" value={formData.country} /> 
                <DetailRow label="Phone" value={formData.phone || 'N/A'} />
                {formData.accountType !== 'teacher' && <DetailRow label="Relationship" value={formData.relationship} />}
            </div>

            <div className="bg-pink-50 p-5 rounded-xl border border-pink-200">
                <h3 className="text-lg font-bold text-pink-600 mb-3 flex items-center gap-2"><Baby className="w-5 h-5" /> Child Profile</h3>
                <DetailRow label="Child's Name" value={formData.childName} />
                <DetailRow label="Birthday" value={formData.childDOB} />
                <DetailRow label="Age" value={`${currentChildAge} years`} /> 
                <DetailRow label="Gender" value={formData.childGender || 'N/A'} /> 
                <DetailRow label="Grade/Level" value={formData.childGrade} />
                <DetailRow label="Learning Style" value={formData.learningStyle} />
                <DetailRow label="Goal" value={formData.learningGoal || 'None specified'} />
            </div>

            <div className="p-4 bg-yellow-100 border border-yellow-300 rounded-xl text-sm text-gray-700 flex items-start gap-3">
                <Award className="w-5 h-5 min-w-5 text-yellow-700 mt-0.5" />
                <p>By clicking "Confirm & Register", you agree to the Terms of Service and Privacy Policy.</p>
            </div>
            
        </motion.div>
    );


    const progressPercentage = (step / 3) * 100;

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-purple-50 to-sky-50 p-4 sm:p-6">
            <motion.div
                className="w-full max-w-lg bg-white p-6 sm:p-10 rounded-3xl shadow-2xl border-t-4 border-pink-500"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
            >
                {/* CONDITIONAL RENDER: SHOW SUCCESS MESSAGE OR THE FORM */}
                <AnimatePresence mode="wait">
                    {isRegistered ? (
                        <SuccessMessage 
                            key="success"
                            childName={formData.childName} 
                            fullName={formData.fullName} 
                        />
                    ) : (
                        <motion.div key="form" className="w-full">
                            <header className="text-center mb-8">
                                <h1 className="text-3xl font-extrabold text-gray-800 tracking-tight">
                                    Complete Registration
                                </h1>
                                <p className="text-gray-500 mt-1">
                                    Please provide details for the caregiver and the student.
                                </p>
                            </header>

                            {/* Progress Bar */}
                            <div className="mb-8">
                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <motion.div
                                        className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                                        initial={{ width: '0%' }}
                                        animate={{ width: `${progressPercentage}%` }}
                                        transition={{ duration: 0.5 }}
                                    />
                                </div>
                                <p className="text-sm text-center font-medium mt-2 text-gray-600">
                                    Step {step} of 3
                                </p>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <AnimatePresence mode="wait">
                                    {step === 1 && StepOne}
                                    {step === 2 && StepTwo}
                                    {step === 3 && StepThree}
                                </AnimatePresence>

                                {/* Navigation Buttons */}
                                <div className="flex justify-between mt-10 pt-4 border-t border-gray-100">
                                    <motion.button
                                        type="button"
                                        onClick={prevStep}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold transition-all ${
                                            step > 1 
                                                ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                                                : 'opacity-0 pointer-events-none'
                                        }`}
                                        whileHover={{ scale: 1.03 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        <ArrowLeft className="w-5 h-5" /> Back
                                    </motion.button>

                                    {step < 3 && (
                                        <motion.button
                                            type="button"
                                            onClick={nextStep}
                                            className="flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-xl font-bold text-lg text-white shadow-lg shadow-purple-300 transition-all ml-auto"
                                            whileHover={{ scale: 1.02, boxShadow: "0 10px 15px -3px rgba(139, 92, 246, 0.4)" }}
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            Next <ArrowRight className="w-5 h-5" />
                                        </motion.button>
                                    )}

                                    {step === 3 && (
                                        <motion.button
                                            type="submit"
                                            className="flex items-center justify-center gap-2 px-6 py-3 bg-pink-600 hover:bg-pink-700 rounded-xl font-bold text-lg text-white shadow-lg shadow-pink-300 transition-all ml-auto disabled:bg-pink-400 disabled:shadow-none"
                                            whileHover={{ scale: isLoading ? 1 : 1.02, boxShadow: isLoading ? 'none' : "0 10px 15px -3px rgba(236, 72, 153, 0.4)" }}
                                            whileTap={{ scale: isLoading ? 1 : 0.98 }}
                                            disabled={isLoading}
                                        >
                                            {isLoading ? (
                                                <>
                                                    {/* Simple Loading Spinner */}
                                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Registering...
                                                </>
                                            ) : (
                                                <>
                                                    <CheckCircle className="w-5 h-5" /> Confirm & Register
                                                </>
                                            )}
                                        </motion.button>
                                    )}
                                </div>
                            </form>
                            
                            <div className="mt-8 text-center">
                                <p className="text-sm text-gray-600">
                                    Already registered? 
                                    <a href="/login" className="font-semibold text-purple-600 hover:text-purple-700 ml-1 transition-colors">
                                        Sign in here!
                                    </a>
                                </p>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
    );
}